import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ArrowLeft, Crown, Star, Gem, BookOpen, Info, Plus, Globe2, Sun, Moon, Cpu, Leaf } from 'lucide-react';
import { getCharacterData, handleCharacterLevelUp, getCharacterLevelInfo } from '../logic/characterLogic';
import HUD from './HUD';
import { PLAYER, ELEMENTS, RESOURCES } from '../data';
import { ASSETS } from '../assetManifest';
import iconMap from '../iconMap';
import { ElementInfoModal } from './ElementInfoModal';

const SkillDetailsModal = ({ skill, onClose }) => {
  useEffect(() => {
    const tl = gsap.timeline();

    tl.fromTo('.skill-details-area',
      { backgroundColor: 'rgba(255, 255, 255, 1)' },
      {
        backgroundColor: 'rgba(255, 255, 255, 0)',
        duration: 0.45,
        ease: 'power2.out'
      }
    );

    tl.from('.skill-details-content', {
      y: -20,
      scale: 0,
      opacity: 0,
      duration: 0.3,
      ease: 'back.out(1.7)'
    });
  }, []);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="skill-details-area w-full py-8 bg-gradient-to-r from-transparent via-black/80 to-transparent">
        <div className="skill-details-content max-w-lg mx-auto p-6 bg-gray-900/90 rounded-2xl">
          <h3 className="text-2xl font-bold text-blue-300 mb-4">{skill.name}</h3>
          <p className="text-gray-300 mb-6">{skill.details}</p>
          <button
            onClick={onClose}
            className="px-8 py-2 rounded-full bg-white text-black font-bold hover:bg-gray-100 transition-colors"
            style={{ fontFamily: 'Norse' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const LevelUpModal = ({ onClose }) => {
  useEffect(() => {
    const tl = gsap.timeline();

    tl.fromTo('.level-up-area',
      { backgroundColor: 'rgba(255, 255, 255, 1)' },
      {
        backgroundColor: 'rgba(255, 255, 255, 0)',
        duration: 0.45,
        ease: 'power2.out'
      }
    );

    tl.from('.level-up-content', {
      y: -20,
      scale: 0,
      opacity: 0,
      duration: 0.3,
      ease: 'back.out(1.7)'
    });
  }, []);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="level-up-area w-full py-8 bg-gradient-to-r from-transparent via-black/80 to-transparent">
        <div className="level-up-content flex flex-col items-center gap-4">
          <div className="bg-gradient-to-b from-white/10 to-white/5 p-8 rounded-2xl">
            {React.createElement(Crown, { size: 48, className: 'text-yellow-400' })}
          </div>
          <div className="text-white text-center">
            <div className="text-2xl font-bold">Level Up!</div>
            <div className="text-sm opacity-70">Your character has grown stronger</div>
          </div>
          <button
            onClick={onClose}
            className="px-12 py-3 rounded-full bg-white text-black font-bold hover:bg-gray-100 transition-colors mt-4"
            style={{ fontFamily: 'Norse' }}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

const ParticleEffect = () => {
  const particlesRef = useRef([]);

  useEffect(() => {
    particlesRef.current.forEach((particle) => {
      gsap.set(particle, {
        y: Math.random() * window.innerHeight,
        x: Math.random() * window.innerWidth
      });

      gsap.to(particle, {
        y: '-=100',
        x: '+=50',
        duration: 'random(2, 4)',
        repeat: -1,
        ease: 'none',
        opacity: 'random(0.2, 0.4)'
      });
    });
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {Array.from({ length: 30 }).map((_, i) => (
        <div
          key={i}
          ref={el => particlesRef.current[i] = el}
          className="absolute w-1.5 h-1.5 bg-white/20 rounded-full"
        />
      ))}
    </div>
  );
};

function Character({ app, characterId }) {
  const characterRef = useRef(null);
  const statsRef = useRef(null);
  const menuRef = useRef(null);
  const nameRef = useRef(null);
  const starsRef = useRef(null);

  const [character, setCharacter] = useState(getCharacterData(characterId));
  const [showLevelUpModal, setShowLevelUpModal] = useState(false);
  const [activeView, setActiveView] = useState('overview');
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);
  const [showAllStats, setShowAllStats] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setCharacter(getCharacterData(characterId));
    }, 10);
    return () => clearInterval(interval);
  }, [characterId]);

  const { canLevelUp, upgradeCost } = getCharacterLevelInfo(character);

  const handleLevelUp = () => {
    const success = handleCharacterLevelUp(character);
    if (success) {
      setShowLevelUpModal(true);
    }
  };

  const formatBonus = (bonus) => {
    if (typeof bonus === 'string') {
      return bonus;
    }
    if (typeof bonus === 'object' && bonus.stat && bonus.increase) {
      return `${bonus.stat} +${bonus.increase * 100}%`;
    }
    return 'Unknown bonus';
  };

  const renderContent = () => {
    switch (activeView) {
      case 'skills':
        return character.skills ? (
          <div className="space-y-4">
            <div className="text-blue-200 mb-4">Total Pulls: {character.pulls}</div>
            {character.skills?.map((skill, index) => (
              <div key={index} className="bg-gray-900/50 p-4 rounded-xl">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-lg font-bold text-blue-300">{skill.name}</div>
                    <div className="text-gray-400 text-sm mt-1">{skill.description}</div>
                  </div>
                  <button
                    onClick={() => setSelectedSkill(skill)}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    {React.createElement(Info, { size: 20, className: 'text-blue-300' })}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null;
      case 'ascension':
        return character.ascensionBonuses ? (
          <div className="space-y-4">
            <div className="text-blue-200 mb-4">Total Pulls: {character.pulls}</div>
            {character.ascensionBonuses?.map((bonus, index) => {
              const isUnlocked = character.pulls >= (bonus.pullsRequired || 0);
              return (
                <div
                  key={index}
                  className={`p-4 rounded-xl ${
                    isUnlocked ? 'bg-blue-900/30' : 'bg-gray-900/50'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className={`text-lg font-bold ${
                        isUnlocked ? 'text-blue-300' : 'text-gray-400'
                      }`}>
                        {bonus.name}
                      </div>
                      <div className={`text-sm mt-1 ${
                        isUnlocked ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                        {formatBonus(bonus.bonus)}
                      </div>
                    </div>
                    {!isUnlocked && (
                      <div className="text-sm text-gray-500">
                        {bonus.pullsRequired} pulls required
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null;
      default:
        const statsEntries = Object.entries(character.stats);
        const displayedStats = showAllStats ? statsEntries : statsEntries.slice(0, 5);
        
        return (
          <div ref={statsRef} className="space-y-3">
            {displayedStats.map(([key, value], index) => {
              const IconComponent = iconMap[key] || (() => (
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-blue-300/20 text-blue-300">
                  {key.charAt(0)}
                </span>
              ));

              return (
                <div key={index} className="flex items-center justify-between text-lg">
                  <div className="flex items-center gap-2">
                    {React.createElement(IconComponent, { size: 20, className: 'text-blue-300' })}
                    <span className="text-blue-100" style={{ fontFamily: 'Space Grotesk' }}>{key}</span>
                  </div>
                  <span className="text-white font-medium" style={{ fontFamily: 'Space Grotesk' }}>{value}</span>
                </div>
              );
            })}
            {statsEntries.length > 5 && (
              <button
                onClick={() => setShowAllStats(!showAllStats)}
                className="w-full mt-2 py-2 text-blue-300 hover:text-blue-200 transition-colors"
              >
                {showAllStats ? 'Show Less' : 'Show More'}
              </button>
            )}
          </div>
        );
    }
  };

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        app.goToMenu();
      }
    };
    window.addEventListener('keydown', handleEsc);

    const tl = gsap.timeline();

    tl.from(menuRef.current.children, {
      y: 100,
      opacity: 0,
      duration: 0.25,
      stagger: 0.05,
      ease: 'back.out(1.4)'
    })
      .from(characterRef.current, {
        x: -300,
        opacity: 0,
        duration: 0.6,
        ease: 'power4.out'
      }, '-=0.2')
      .from(nameRef.current.children, {
        y: -30,
        opacity: 0,
        duration: 0.4,
        stagger: 0.05,
        ease: 'back.out(1.7)'
      }, '-=0.3')
      .from(starsRef.current.children, {
        scale: 0,
        opacity: 0,
        duration: 0.2,
        stagger: 0.05,
        ease: 'back.out(2)'
      }, '-=0.2')
      .from(statsRef.current.children, {
        x: 100,
        opacity: 0,
        duration: 0.3,
        stagger: 0.08,
        ease: 'power2.out'
      }, '-=0.1');

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, []);

  return (
    <div className="bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#1a1a2e] h-full w-full mx-auto relative overflow-hidden text-white">
      <ParticleEffect />
      <div className="absolute inset-0 bg-[url('/assets/nine-realms.png')] opacity-5 bg-cover bg-center" />

      <div className="p-6 flex flex-col h-full relative">
        <div ref={characterRef} className="absolute left-0 top-[100px] h-[800px] z-0">
          <img
            src={ASSETS.characters[character.prototypeId].path}
            alt={character.name}
            className="absolute h-[850px] -left-[25px] -top-[25px] opacity-20 blur-sm"
          />
          <img
            src={ASSETS.characters[character.prototypeId].path}
            alt={character.name}
            className="h-full object-contain relative z-10"
          />
        </div>

        <div className="ml-auto w-full md:w-[40%] lg:w-[30%] mt-8 z-20">
          <div ref={nameRef} className="mb-6">
            <h2 className="text-4xl font-bold mb-2 text-[#a3e4f6] drop-shadow-[0_0_10px_rgba(163,228,246,0.5)]"
                style={{ fontFamily: 'Norse' }}>
              {character.name}
            </h2>
            <div className="flex flex-wrap gap-2 mb-2">
              {character.tags.map((tag, index) => (
                <span key={index}
                      className={`px-3 py-1 rounded-full bg-${tag.toLowerCase()}-500/30 border border-${tag.toLowerCase()}-400/30 text-${tag.toLowerCase()}-300 text-sm`}
                      style={{ fontFamily: 'Futhark' }}>
                  {tag}
                </span>
              ))}
              {character.elements.map((element, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedElement(element)}
                  className={`p-2 rounded-full ${ELEMENTS[element].color} hover:opacity-80 transition-opacity`}
                >
                  {React.createElement(iconMap[element], { size: 20, fill: 'currentColor' })}
                </button>
              ))}
            </div>
            <div ref={starsRef} className="flex gap-1 mb-2">
              {[...Array(character.rarity)].map((_, i) => (
                React.createElement(Star, {
                  key: i,
                  size: 16,
                  fill: 'currentColor',
                  className: 'text-yellow-400 drop-shadow-[0_0_5px_rgba(255,215,0,0.5)]'
                })
              ))}
            </div>
            <div className="text-3xl font-bold text-[#a3e4f6]/80" style={{ fontFamily: 'Space Grotesk' }}>
              Level {character.level}{character.maxLevel ? ` / ${character.maxLevel}` : ''}
            </div>
            <div className="h-px bg-gradient-to-r from-transparent via-blue-300/50 to-transparent my-6" />
          </div>

          {renderContent()}

          {(!character.maxLevel || character.level < character.maxLevel) && activeView === 'overview' && (
            <div className="mt-6 space-y-3">
              <div className="bg-gray-900/50 p-4 rounded-xl">
                <div className="text-sm text-gray-400 mb-2">Required Resources</div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {React.createElement(iconMap[RESOURCES.currency.id], {
                      size: 18,
                      className: `text-${RESOURCES.currency.color}-400`
                    })}
                    <span className="text-blue-200">{RESOURCES.currency.name}</span>
                  </div>
                  <span className={!canLevelUp ? 'text-red-400' : 'text-blue-200'}>
                    {upgradeCost.toLocaleString()} / {PLAYER.resources.currency.toLocaleString()}
                  </span>
                </div>
              </div>

              <button
                onClick={handleLevelUp}
                disabled={!canLevelUp}
                className={`w-full py-3 rounded-xl font-bold transition-all ${
                  canLevelUp
                    ? 'bg-gradient-to-r from-blue-600 to-blue-400 hover:from-blue-500 hover:to-blue-300 text-white shadow-lg shadow-blue-500/25'
                    : 'bg-gray-600/50 text-gray-400 cursor-not-allowed'
                }`}
                style={{ fontFamily: 'Norse' }}
              >
                Level Up
              </button>
            </div>
          )}
        </div>

        <div ref={menuRef} className="absolute bottom-24 left-6 md:left-24 space-y-2">
          {[
            { text: 'Overview', icon: BookOpen, view: 'overview' },
            ...(character.skills ? [{ text: 'Skills', icon: Star, view: 'skills' }] : []),
            ...(character.ascensionBonuses ? [{ text: 'Ascension', icon: Crown, view: 'ascension' }] : [])
          ].map((item, index) => {
            const IconComponent = item.icon;
            const isActive = activeView === item.view;
            return (
              <button
                key={index}
                onClick={() => setActiveView(item.view)}
                className={`flex items-center gap-2 p-2 text-sm rounded transition-colors w-32 justify-between group ${
                  isActive ? 'bg-[#a3e4f6]/20' : 'text-[#a3e4f6] hover:bg-[#a3e4f6]/10'
                }`}
                style={{ fontFamily: 'Futhark' }}
              >
                <span className="flex items-center gap-2">
                  {React.createElement(IconComponent, {
                    size: 16,
                    className: `text-[#a3e4f6] ${!isActive && 'group-hover:text-white'} transition-colors`
                  })}
                  <span
                    className={!isActive ? 'group-hover:text-white transition-colors' : 'text-white'}>{item.text}</span>
                </span>
                {isActive && <span className="w-1.5 h-1.5 rounded-full bg-[#a3e4f6]"></span>}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => app.goToMenu()}
          className="absolute bottom-6 left-6 z-20 flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1e3a8a]/80 to-[#3b82f6]/80 rounded-full text-[#a3e4f6] hover:text-white transition-colors shadow-lg hover:shadow-[#3b82f6]/20 backdrop-blur-sm border border-[#3b82f6]/20"
          style={{ fontFamily: 'Futhark' }}
        >
          {React.createElement(ArrowLeft, { size: 20 })}
          <span>Return to Menu</span>
        </button>
      </div>

      {showLevelUpModal && (
        <LevelUpModal onClose={() => setShowLevelUpModal(false)} />
      )}

      {selectedSkill && (
        <SkillDetailsModal skill={selectedSkill} onClose={() => setSelectedSkill(null)} />
      )}

      {selectedElement && (
        <ElementInfoModal element={selectedElement} onClose={() => setSelectedElement(null)} />
      )}
    </div>
  );
}

export default Character;
